/**
 * El campo «N.º de expediente» de una orden nueva admite VARIOS (04/11, pedido del
 * usuario): los timbres fiscales de tres expedientes son tres órdenes iguales, y se
 * redactan de una vez (`POST /ordenes-pago/por-expediente`).
 *
 * ⚠ **Se separan con coma, punto y coma o salto de línea; NUNCA con un espacio.** «2026
 * 119» es un número mal tecleado, no dos expedientes: partirlo crearía dos órdenes, una
 * para «2026» y otra para «119». Con el espacio prohibido dentro de un número, lo dice
 * el campo y no se crea nada.
 *
 * ⚠ **El servidor es quien normaliza y guarda** (`alcance_ordenes.normalizar_expediente`):
 * esto sólo ENSEÑA cómo quedará y no deja enviar lo que va a rechazar. Lo que se manda es
 * cada pedazo tal como se tecleó.
 */

/** Lo mismo que `alcance_ordenes._EXPEDIENTE` del servidor. */
const EXPEDIENTE = /^[A-Za-z0-9][A-Za-z0-9/-]{0,19}$/

/** Lo mismo que `MAXIMO_EXPEDIENTES` del servidor. */
export const MAXIMO_EXPEDIENTES = 20

/** El número como lo guarda el servidor: sin el «EXP» que se teclea por costumbre. */
export function limpiarExpediente(texto: string): string {
  return texto
    .trim()
    .replace(/^exp(?:ediente)?\.?\s*/i, "")
    .trim()
    .toUpperCase()
}

export type Expedientes = {
  /** Cada uno tal como se tecleó, para mandarlo. */
  tecleados: string[]
  /** Cada uno como se guardará, en el mismo orden. */
  numeros: string[]
  /** Los que no son un número de expediente. */
  invalidos: string[]
  /** Los que salen dos veces, ya normalizados: serían dos órdenes por el mismo gasto. */
  repetidos: string[]
}

export function leerExpedientes(texto: string): Expedientes {
  const tecleados = texto
    .split(/[,;\n\r]+/)
    .map((pedazo) => pedazo.trim())
    .filter((pedazo) => pedazo !== "")
  const numeros = tecleados.map(limpiarExpediente)
  const invalidos = numeros.filter((n) => !EXPEDIENTE.test(n))
  const repetidos = [...new Set(numeros.filter((n, i) => numeros.indexOf(n) !== i))]
  return { tecleados, numeros, invalidos, repetidos }
}

/** Si con esto se puede crear: al menos uno, todos válidos, sin repetir y sin pasar del tope. */
export function expedientesValidos(e: Expedientes): boolean {
  return (
    e.numeros.length > 0 &&
    e.numeros.length <= MAXIMO_EXPEDIENTES &&
    e.invalidos.length === 0 &&
    e.repetidos.length === 0
  )
}

/** Lo que se pone delante de la descripción con el más largo: el que decide si cabe. */
export function prefijoMasLargo(numeros: string[]): string {
  const largo = numeros.reduce((mayor, n) => (n.length > mayor.length ? n : mayor), "")
  return largo === "" ? "" : `EXP ${largo} · `
}
